import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { LayoutGrid, List, Plus } from 'lucide-react';
import Layout from '../components/Layout';
import PropertyCard from '../components/PropertyCard';
import PropertyListItem from '../components/PropertyListItem';
import PropertyModal from '../components/PropertyModal';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import { Button } from '../components/ui/button';
import { getApiUrl } from '../config/api';
import authFetch from '../utils/authFetch';

const Properties = () => {
  const location = useLocation();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [viewMode, setViewMode] = useState('grid');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [saving, setSaving] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);

  const formatDate = (iso) => {
    if (!iso) return '--';
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  const fetchProperties = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authFetch(getApiUrl('/api/v1/property/list'));
      if (!res.ok) throw new Error(`Server responded with ${res.status}`);
      const json = await res.json();
      setProperties(json.properties || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    if (location.state?.openAddModal) {
      setEditingProperty(null);
      setModalOpen(true);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const handleAddProperty = () => {
    setEditingProperty(null);
    setModalOpen(true);
  };

  const handleEditProperty = (property) => {
    setEditingProperty(property);
    setModalOpen(true);
  };

  const handleDeleteProperty = (property) => {
    setPropertyToDelete(property);
    setDeleteDialogOpen(true);
  };

  const handleSaveProperty = async (propertyData) => {
    setSaving(true);
    try {
      const url = editingProperty
        ? getApiUrl('/api/v1/property/update')
        : getApiUrl('/api/v1/property/create');

      const method = editingProperty ? 'PUT' : 'POST';

      const body = {
        property_name: propertyData.name,
        property_address: propertyData.address,
        description: propertyData.description || '',
        property_bedrooms: propertyData.bedrooms ?? 0,
        property_bathrooms: propertyData.bathrooms ?? 0,
        property_type: propertyData.property_type || '',
      };

      if (editingProperty) {
        body.property_id = editingProperty.id;
      }

      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error(`Server responded ${res.status}`);

      setModalOpen(false);
      setEditingProperty(null);
      await fetchProperties();
    } catch (err) {
      console.error(`Failed to ${editingProperty ? 'update' : 'create'} property:`, err);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!propertyToDelete) return;
    try {
      const res = await authFetch(
        getApiUrl(`/api/v1/property/delete/${propertyToDelete.id}`),
        { method: 'DELETE' }
      );
      if (!res.ok) throw new Error(`Server responded ${res.status}`);
      setDeleteDialogOpen(false);
      setPropertyToDelete(null);
      await fetchProperties();
    } catch (err) {
      console.error('Failed to delete property:', err);
    }
  };

  return (
    <Layout pageTitle="Your Properties">
      <div className="space-y-6">
        {/* Header row: count + controls */}
        <div className="flex items-center justify-between">
          <div>
            {!loading && !error && (
              <p className="text-[#64748B]">
                {properties.length} active{' '}
                {properties.length === 1 ? 'property' : 'properties'}
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* View toggle */}
            <div className="flex items-center gap-1 bg-white border border-[#E2E8F0] rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-[#16A34A] text-white'
                    : 'text-[#64748B] hover:bg-[#F1F5F9]'
                }`}
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'list'
                    ? 'bg-[#16A34A] text-white'
                    : 'text-[#64748B] hover:bg-[#F1F5F9]'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            <Button
              onClick={handleAddProperty}
              className="bg-[#16A34A] hover:bg-[#15803D] text-white gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Property
            </Button>
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <p className="text-[#64748B] text-center py-12">Loading…</p>
        )}

        {/* Error state */}
        {error && (
          <p className="text-red-600 text-center py-12">{error}</p>
        )}

        {/* Empty state */}
        {!loading && !error && properties.length === 0 && (
          <div className="bg-white rounded-lg border border-[#E2E8F0] p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-[#F1F5F9] rounded-full flex items-center justify-center mx-auto mb-4">
                <LayoutGrid className="w-8 h-8 text-[#64748B]" />
              </div>
              <h3 className="text-[#030712] font-medium mb-2">No properties yet</h3>
              <p className="text-sm text-[#64748B] mb-6">
                Get started by adding your first rental property
              </p>
              <Button
                onClick={handleAddProperty}
                className="bg-[#16A34A] hover:bg-[#15803D] text-white gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Property
              </Button>
            </div>
          </div>
        )}

        {/* Grid view */}
        {!loading && !error && properties.length > 0 && viewMode === 'grid' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((prop) => (
              <PropertyCard
                key={prop.id}
                property={prop}
                onEdit={handleEditProperty}
                onDelete={handleDeleteProperty}
                formatDate={formatDate}
              />
            ))}
          </div>
        )}

        {/* List view */}
        {!loading && !error && properties.length > 0 && viewMode === 'list' && (
          <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden">
            <div className="bg-[#F8FAFC] border-b border-[#E2E8F0] px-6 py-3">
              <div className="flex items-center gap-4">
                <div className="w-10" />
                <div className="flex-1 grid grid-cols-[2fr_2fr_1fr_2fr_1fr] gap-4 text-xs text-[#64748B] uppercase tracking-wide font-medium">
                  <div>Property</div>
                  <div>Details</div>
                  <div>Added</div>
                  <div>Description</div>
                  <div />
                </div>
              </div>
            </div>
            {properties.map((prop) => (
              <PropertyListItem
                key={prop.id}
                property={prop}
                onEdit={handleEditProperty}
                onDelete={handleDeleteProperty}
                formatDate={formatDate}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit modal */}
      <PropertyModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingProperty(null);
        }}
        onSave={handleSaveProperty}
        property={editingProperty}
        saving={saving}
      />

      {/* Delete confirmation */}
      <DeleteConfirmDialog
        isOpen={deleteDialogOpen}
        property={propertyToDelete}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setPropertyToDelete(null);
        }}
      />
    </Layout>
  );
};

export default Properties;
